import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { sendToWebhook, formatLeadData, trackPixelEvent } from '../utils/tracking';
import { professions } from '../utils/professionList';
import { countryCodes } from '../utils/countryCodes';

const { FiX, FiUser, FiMail, FiPhone, FiBriefcase, FiArrowRight, FiCheckCircle, FiAlertCircle, FiChevronDown } = FiIcons;

const LeadCaptureModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    profession: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      // Track modal view
      trackPixelEvent('ViewContent', {
        content_name: 'Lead Capture Modal',
        content_category: 'Modal'
      });
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.profession) {
      setError('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    const phoneRegex = /^[0-9]{6,15}$/;
    const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      setError('Please enter a valid phone number (6-15 digits)');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const fullPhoneNumber = `${formData.countryCode}${cleanPhone}`;
      
      const leadData = formatLeadData(
        {
          ...formData,
          phone: fullPhoneNumber
        },
        'lead_capture_modal'
      );

      // Track Meta Pixel Lead event
      trackPixelEvent('Lead', {
        content_name: 'Lead Capture Form',
        content_category: 'Lead Generation',
        value: 0,
        currency: 'INR'
      });

      const webhookResponse = await sendToWebhook(leadData);

      if (webhookResponse.success) {
        console.log('Lead captured successfully:', leadData);
        
        // Track successful lead submission
        trackPixelEvent('CompleteRegistration', {
          content_name: 'Lead Form Submission',
          status: 'success'
        });
        
        setIsSuccess(true);

        setTimeout(() => {
          setIsSuccess(false);
          setFormData({
            name: '',
            email: '',
            countryCode: '+91',
            phone: '',
            profession: ''
          });
          onClose();
        }, 2500);
      } else {
        throw new Error(webhookResponse.error || 'Failed to submit form');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative bg-white rounded-2xl p-6 md:p-8 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors z-10"
              aria-label="Close modal"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>

            {isSuccess ? (
              <motion.div
                className="text-center py-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600 text-3xl" />
                </div>
                <h3 className="font-serif text-2xl font-bold mb-2 text-gray-900">
                  Success!
                </h3>
                <p className="text-gray-600">
                  We'll be in touch soon with your exclusive Black Friday details.
                </p>
              </motion.div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiUser} className="text-primary-blue text-2xl" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold mb-2 text-gray-900">
                    Interested in <span className="text-primary-blue">My Webinar?</span>
                  </h3>
                  <p className="text-gray-600">
                    Share your details to unlock exclusive Black Friday insights and early access benefits!
                  </p>
                </div>

                {error && (
                  <motion.div
                    className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start space-x-2"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <SafeIcon icon={FiAlertCircle} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Full Name */}
                  <div className="relative">
                    <SafeIcon
                      icon={FiUser}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                    />
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-3 text-gray-900 placeholder-gray-400 focus:border-primary-blue focus:outline-none transition-colors"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="relative">
                    <SafeIcon
                      icon={FiMail}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-3 text-gray-900 placeholder-gray-400 focus:border-primary-blue focus:outline-none transition-colors"
                      required
                    />
                  </div>

                  {/* Phone with Country Code */}
                  <div className="relative flex space-x-2">
                    <div className="relative w-32">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-3 pr-8 py-3 text-gray-900 focus:border-primary-blue focus:outline-none transition-colors appearance-none text-sm"
                        required
                      >
                        {countryCodes.map((country, index) => (
                          <option key={index} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <SafeIcon
                        icon={FiChevronDown}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                    <div className="relative flex-1">
                      <SafeIcon
                        icon={FiPhone}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                      />
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number *"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-4 py-3 text-gray-900 placeholder-gray-400 focus:border-primary-blue focus:outline-none transition-colors"
                        required
                      />
                    </div>
                  </div>

                  {/* Profession Dropdown */}
                  <div className="relative">
                    <SafeIcon
                      icon={FiBriefcase}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                    />
                    <select
                      name="profession"
                      value={formData.profession}
                      onChange={handleInputChange}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg pl-10 pr-10 py-3 text-gray-900 focus:border-primary-blue focus:outline-none transition-colors appearance-none"
                      required
                    >
                      <option value="">Select Profession *</option>
                      {professions.map((profession, index) => (
                        <option key={index} value={profession}>
                          {profession}
                        </option>
                      ))}
                    </select>
                    <SafeIcon
                      icon={FiChevronDown}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary-blue hover:bg-dark-blue text-white font-bold py-4 rounded-lg transition-all hover-glow-strong flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{isSubmitting ? 'Submitting...' : 'Get Exclusive Access'}</span>
                    {!isSubmitting && <SafeIcon icon={FiArrowRight} />}
                  </motion.button>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    🔒 Your information is secure and will never be shared
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadCaptureModal;